import { absolutize, normalize, parsePath } from 'path-data-parser'
import { Segment } from 'path-data-parser/lib/parser'
import { Point } from '../diagram/point.ts'

function getPointOnBezierCurve(
    start: Point,
    control1: Point,
    control2: Point,
    end: Point,
    t: number): Point {
    return {
        x: ((1 - t) ** 3) * start.x +
            t * control1.x * 3 * ((1 - t) ** 2) +
            control2.x * 3 * (1 - t) * t ** 2 +
            end.x * t ** 3,
        y: ((1 - t) ** 3) * start.y +
            t * control1.y * 3 * ((1 - t) ** 2) +
            control2.y * 3 * (1 - t) * t ** 2 +
            end.y * t ** 3
    }
}

// count the number of points on each segment
function calculateLengths(segments: Segment[]): number[] {
    const lengths: number[] = []
    let lastPoint: Point = { x: 0, y: 0 }

    // M L and C symbols corresponds to certain command in svg path
    // M - move a point to coordinate
    // L - draw a line from the last point
    // C - draw a Bezier curve
    for (const { key, data } of segments) {
        switch (key) {
        case 'M':
            lengths.push(1)
            lastPoint = { x: data[0], y: data[1] }
            break
        case 'L': {
            lengths.push(Math.floor(Math.hypot(data[0] - lastPoint.x, data[1] - lastPoint.y)))
            lastPoint = { x: data[0], y: data[1] }
            break
        }
        case 'C': {
            const control1 = { x: data[0], y: data[1] }
            const control2 = { x: data[2], y: data[3] }
            const end = { x: data[4], y: data[5] }

            // Rough approximation of the points count on Bezier curve, basically calculating lengths of straight lines
            lengths.push(Math.floor((
                Math.hypot(control1.x - lastPoint.x, control1.y - lastPoint.y) +
                    Math.hypot(control2.x - control1.x, control2.y - control1.y) +
                    Math.hypot(end.x - control2.x, end.y - control2.y) +
                    Math.hypot(end.x - lastPoint.x, end.y - lastPoint.y)
            ) / 2))

            lastPoint = end
            break
        }
        }
    }

    return lengths
}

export function getPointOnPath(path: string, t: number): Point {
    const segments = parsePath(path)
    const normalized = normalize(absolutize(segments))

    const segmentsLengths = calculateLengths(normalized)
    const totalLength = segmentsLengths.reduce((accum, currentValue) => accum + currentValue, 0)
    const pointIdxToCalculate = Math.floor(totalLength * t)
    let segmentIdx = 0
    let lengthAccum = segmentsLengths[0]
    for (let i = 1; i < segmentsLengths.length; i++) {
        if (lengthAccum > pointIdxToCalculate) {
            break
        }

        segmentIdx++
        lengthAccum += segmentsLengths[i]
    }
    const segment = normalized[segmentIdx]

    // scaled t parameter to match found segment
    const tSegment = ((pointIdxToCalculate - lengthAccum + segmentsLengths[segmentIdx]) / segmentsLengths[segmentIdx])
    const lastDataLength = normalized[segmentIdx - 1].data.length
    const lastPoint: Point = {
        x: normalized[segmentIdx - 1].data[lastDataLength - 2],
        y: normalized[segmentIdx - 1].data[lastDataLength - 1]
    }
    let resultPoint: Point = { x: 0, y: 0 }

    switch (segment.key) {
    case 'M':
        break
    case 'L': {
        resultPoint = {
            x: lastPoint.x + (segment.data[0] - lastPoint.x) * tSegment,
            y: lastPoint.y + (segment.data[1] - lastPoint.y) * tSegment
        }

        break
    }
    case 'C': {
        const control1 = { x: segment.data[0], y: segment.data[1] }
        const control2 = { x: segment.data[2], y: segment.data[3] }
        const end = { x: segment.data[4], y: segment.data[5] }

        resultPoint = getPointOnBezierCurve(
            lastPoint,
            control1,
            control2,
            end,
            tSegment
        )
        break
    }
    }

    return resultPoint
}
